/**
 * Password-gated proposal viewer.
 *
 * Route: /proposal/:slug   (currently: tokyotokyo | essec)
 *
 * Behaviour:
 *   1. Load meta from GET /api/proposal/:slug — 404 → generic "not available".
 *   2. Show a satisFIND-branded password gate.
 *   3. POST /api/proposal/:slug/login — on success the backend sets a
 *      slug-scoped HttpOnly cookie for 24h and we immediately fetch the
 *      HTML via GET /api/proposal/:slug/content.
 *   4. HTML is injected inside a same-origin iframe (via srcDoc) so its
 *      internal CSS + JavaScript run in an isolated document without
 *      touching the surrounding React tree.
 *
 * The proposal HTML is NEVER present in the page source before login —
 * the backend refuses the /content endpoint until the cookie is valid.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProposalGate() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  // Sept 2026 — Tokyo Tokyo hosts multiple dated versions under the
  // same slug. `?v=<variant>` selects one; absent means "index". The
  // backend whitelists variants — unknown values silently fall back to
  // the default index page.
  const variant = searchParams.get('v') || '';
  const [meta, setMeta] = useState(null);        // { title } or null
  const [status, setStatus] = useState('loading'); // loading | gate | unlocked | invalid
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [html, setHtml] = useState(null);
  const iframeRef = useRef(null);

  const contentUrl = () => {
    const base = `${API}/proposal/${encodeURIComponent(slug)}/content`;
    return variant ? `${base}?v=${encodeURIComponent(variant)}` : base;
  };

  // Step 1 — resolve meta or 404.
  useEffect(() => {
    let cancelled = false;
    // Reset unlocked HTML when the variant changes so the iframe swaps
    // to the newly-requested version instead of showing a stale doc.
    setHtml(null);
    setStatus('loading');
    (async () => {
      try {
        const res = await fetch(`${API}/proposal/${encodeURIComponent(slug)}`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (!res.ok) {
          setStatus('invalid');
          return;
        }
        const d = await res.json();
        setMeta(d);
        // Try fetching content immediately — if a valid cookie is present
        // from an earlier session, skip the gate.
        const c = await fetch(contentUrl(), { credentials: 'include' });
        if (cancelled) return;
        if (c.ok) {
          const text = await c.text();
          setHtml(text);
          setStatus('unlocked');
        } else {
          setStatus('gate');
        }
      } catch {
        if (!cancelled) setStatus('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !password) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/proposal/${encodeURIComponent(slug)}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        toast.error('Incorrect password. Please try again.');
        setPassword('');
        return;
      }
      const c = await fetch(contentUrl(), { credentials: 'include' });
      if (!c.ok) {
        toast.error('Unable to load the proposal. Please try again.');
        return;
      }
      const text = await c.text();
      setHtml(text);
      setStatus('unlocked');
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Unlocked view ─────────────────────────────────────────────────
  if (status === 'unlocked' && html != null) {
    return (
      <div className="w-screen h-screen bg-white" data-testid="proposal-unlocked">
        <iframe
          ref={iframeRef}
          title={meta?.title || 'Proposal'}
          srcDoc={html}
          className="w-full h-full border-0"
          // sandbox omitted intentionally — we host the HTML ourselves and
          // it needs full JS + same-origin print support to work.
        />
      </div>
    );
  }

  // ── Invalid slug ──────────────────────────────────────────────────
  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1B2E] text-white px-6">
        <div className="max-w-md text-center space-y-4" data-testid="proposal-invalid">
          <BrandMark />
          <p className="text-lg text-white/85">This proposal is not currently available.</p>
        </div>
      </div>
    );
  }

  // ── Loading skeleton ──────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B1B2E]">
        <div className="animate-pulse text-white/60 text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }

  // ── Password gate ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1B2E] via-[#0F2340] to-[#0B1B2E] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 space-y-6" data-testid="proposal-gate">
        <BrandMark dark />
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#64748B] font-bold">
            Confidential Proposal
          </p>
          <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A] leading-tight">
            {meta?.title}
          </h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="proposal-password" className="block text-xs font-semibold text-[#334155] mb-1.5">
              Access password
            </label>
            <input
              id="proposal-password"
              type="password"
              autoFocus
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1A3E6F] focus:border-[#1A3E6F]"
              data-testid="proposal-password-input"
              disabled={submitting}
              required
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !password}
            className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
            data-testid="proposal-submit-button"
          >
            {submitting ? 'Verifying…' : 'View proposal'}
          </button>
        </form>
        <p className="text-[11px] text-[#64748B] leading-relaxed">
          This proposal is intended for the addressed recipient only. All
          content is confidential to satisFIND and the recipient.
        </p>
      </div>
    </div>
  );
}

function BrandMark({ dark = false }) {
  return (
    <div className="flex items-center">
      <img
        src="/satisfind-logo.png"
        alt="satisFIND"
        style={{
          height: 40,
          width: 'auto',
          // Invert to white on the dark gate background so the blue logo stays visible.
          filter: dark ? 'none' : 'brightness(0) invert(1)',
        }}
      />
    </div>
  );
}
